import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, IconButton, Stack, TextField, Tooltip, Typography} from 'views/components';
import {AddOutlined, DeleteOutline, KeyboardArrowDown, KeyboardArrowUp} from 'views/icons';
import {Translate, translate} from 'views/module';

// Mirrors the engine's Joi on PUT /domain_guide/{rn}/rubric so admins never
// hit a raw 400: passingDefinition ≤5000, ≤20 categories, label and key ≤200,
// description ≤1000, conditions ≤500 each. Beyond the caps the engine also
// requires unique category keys, keys containing a non-digit, and unique
// auto-fail conditions — all enforced below before save.
const PASSING_DEFINITION_MAX_CHARS = 5000;
const MAX_FAILURE_CATEGORIES = 20;
const CATEGORY_LABEL_MAX_CHARS = 200;
const CATEGORY_KEY_MAX_CHARS = 200;
const CATEGORY_DESCRIPTION_MAX_CHARS = 1000;
const CONDITION_MAX_CHARS = 500;

// Stable snake_case keys generated from labels; the key never changes once a
// category exists, even if its label is edited later.
const toSnakeCaseKey = (label) => {
  return `${label || ''}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
};

// Client-only row identity so React keys survive reordering; never sent to
// the engine.
let nextRowId = 0;
const newRowId = () => {
  nextRowId += 1;
  return `row-${nextRowId}`;
};

// The order of both lists is semantic: when several categories (or
// conditions) fail on one case, the derived FAIL is attributed to the first
// one in rubric order, so the starter puts the most severe first.
const buildStarterCategories = () => ([
  {
    rowId: newRowId(),
    key: 'missed_escalation',
    label: 'Missed escalation',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.MissedEscalation'),
  },
  {
    rowId: newRowId(),
    key: 'policy_violation',
    label: 'Policy violation',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.PolicyViolation'),
  },
  {
    rowId: newRowId(),
    key: 'wrong_information',
    label: 'Wrong information',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.WrongInformation'),
  },
  {
    rowId: newRowId(),
    key: 'overpromised_too_agreeable',
    label: 'Overpromised / too agreeable',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.Overpromised'),
  },
  {
    rowId: newRowId(),
    key: 'poor_tone',
    label: 'Poor tone',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.PoorTone'),
  },
  {
    rowId: newRowId(),
    key: 'no_follow_up',
    label: 'No follow-up',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.NoFollowUp'),
  },
]);

// Conditions are matched by their exact text when the teacher's checklist is
// parsed, so each starter is a self-contained yes/no statement.
const buildStarterConditions = () => ([
  {
    rowId: newRowId(),
    text: translate('domainGuide.Detail.Rubric.StarterConditions.MissedEscalation'),
  },
  {
    rowId: newRowId(),
    text: translate('domainGuide.Detail.Rubric.StarterConditions.PolicyViolation'),
  },
]);

const buildEditorState = (rubric) => {
  if (!rubric) {
    return {
      passingDefinition: '',
      categories: buildStarterCategories(),
      conditions: buildStarterConditions(),
    };
  }

  return {
    passingDefinition: rubric.passingDefinition || '',
    categories: (rubric.failureCategories || []).map((category) => ({
      rowId: newRowId(),
      key: category.key,
      label: category.label,
      description: category.description || '',
    })),
    conditions: (Array.isArray(rubric.autoFailConditions) ? rubric.autoFailConditions : [])
        .map((condition) => ({
          rowId: newRowId(),
          text: condition,
        })),
  };
};

const moveItem = (items, index, delta) => {
  const targetIndex = index + delta;

  if (targetIndex < 0 || targetIndex >= items.length) {
    return items;
  }

  const reordered = [...items];
  [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];

  return reordered;
};

const fieldLabelSx = {
  fontWeight: 500,
  fontSize: '11px',
  lineHeight: '14px',
  color: '#A8A49E',
};

const sectionHintSx = {
  fontSize: '12px',
  lineHeight: '16px',
  color: '#6B6761',
};

const DomainGuideRubric = (props) => {
  const {
    domainGuideData,
    updateRubric,
  } = props;

  const [editorState, setEditorState] = React.useState(() => buildEditorState(domainGuideData.rubric));
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveFailed, setSaveFailed] = React.useState(false);

  React.useEffect(() => {
    setEditorState(buildEditorState(domainGuideData.rubric));
  }, [domainGuideData.rubric]);

  const setPassingDefinition = (passingDefinition) => {
    setEditorState((currentState) => ({
      ...currentState,
      passingDefinition,
    }));
  };

  const setCategoryField = (index, field, value) => {
    setEditorState((currentState) => ({
      ...currentState,
      categories: currentState.categories.map((category, categoryIndex) => (
        categoryIndex === index ?
          {
            ...category,
            [field]: value,
          } :
          category
      )),
    }));
  };

  const addCategory = () => {
    setEditorState((currentState) => ({
      ...currentState,
      categories: [
        ...currentState.categories,
        {
          rowId: newRowId(),
          key: '',
          label: '',
          description: '',
        },
      ],
    }));
  };

  const removeCategory = (index) => {
    setEditorState((currentState) => ({
      ...currentState,
      categories: currentState.categories.filter((category, categoryIndex) => categoryIndex !== index),
    }));
  };

  const moveCategory = (index, delta) => {
    setEditorState((currentState) => ({
      ...currentState,
      categories: moveItem(currentState.categories, index, delta),
    }));
  };

  const setConditionText = (index, text) => {
    setEditorState((currentState) => ({
      ...currentState,
      conditions: currentState.conditions.map((condition, conditionIndex) => (
        conditionIndex === index ?
          {
            ...condition,
            text,
          } :
          condition
      )),
    }));
  };

  const addCondition = () => {
    setEditorState((currentState) => ({
      ...currentState,
      conditions: [
        ...currentState.conditions,
        {
          rowId: newRowId(),
          text: '',
        },
      ],
    }));
  };

  const removeCondition = (index) => {
    setEditorState((currentState) => ({
      ...currentState,
      conditions: currentState.conditions.filter((condition, conditionIndex) => conditionIndex !== index),
    }));
  };

  const moveCondition = (index, delta) => {
    setEditorState((currentState) => ({
      ...currentState,
      conditions: moveItem(currentState.conditions, index, delta),
    }));
  };

  const trimmedConditions = editorState.conditions.map((condition) => condition.text.trim());
  const nonEmptyConditions = trimmedConditions.filter(Boolean);
  const hasDuplicateConditions = new Set(nonEmptyConditions).size !== nonEmptyConditions.length;
  const canAddCategory = editorState.categories.length < MAX_FAILURE_CATEGORIES;
  const canSave = Boolean(editorState.passingDefinition.trim()) &&
    editorState.categories.length <= MAX_FAILURE_CATEGORIES &&
    editorState.categories.every((category) => category.label.trim()) &&
    trimmedConditions.every(Boolean) &&
    !hasDuplicateConditions;

  const saveRubric = async () => {
    if (!canSave || isSaving) {
      return;
    }

    const usedKeys = new Set(editorState.categories.map((category) => category.key).filter(Boolean));
    const failureCategories = editorState.categories.map((category) => {
      let key = category.key;

      if (!key) {
        // Leave headroom for the dedupe suffix under the engine's key cap,
        // and prefix all-digit keys — the engine rejects them because JS
        // objects iterate integer-like keys numerically first, which would
        // scramble the stored checks' key order.
        let baseKey = (toSnakeCaseKey(category.label) || 'category').slice(0, CATEGORY_KEY_MAX_CHARS - 20);

        if (!/\D/.test(baseKey)) {
          baseKey = `category_${baseKey}`;
        }
        key = baseKey;
        let suffix = 2;

        while (usedKeys.has(key)) {
          key = `${baseKey}_${suffix}`;
          suffix += 1;
        }
        usedKeys.add(key);
      }

      return {
        key,
        label: category.label.trim(),
        description: category.description,
      };
    });

    setIsSaving(true);
    setSaveFailed(false);

    const saved = await updateRubric({
      passingDefinition: editorState.passingDefinition,
      failureCategories,
      autoFailConditions: editorState.conditions.map((condition) => condition.text.trim()),
    });

    setSaveFailed(!saved);
    setIsSaving(false);
  };

  const rubricUpdatedAtDate = new Date(domainGuideData.rubricUpdatedAt);
  const rubricUpdatedAtLabel = Number.isNaN(rubricUpdatedAtDate.getTime()) ?
    '' :
    rubricUpdatedAtDate.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

  const renderMoveButtons = (index, itemCount, moveHandler, upLabel, downLabel) => (
    <Stack sx={{flexShrink: 0}}>
      <IconButton
        aria-label={upLabel}
        disabled={index === 0}
        onClick={() => moveHandler(index, -1)}
        size='small'
        sx={{p: '2px'}}
      >
        <KeyboardArrowUp sx={{fontSize: 16, color: index === 0 ? '#E8E4DE' : '#A8A49E'}} />
      </IconButton>
      <IconButton
        aria-label={downLabel}
        disabled={index === itemCount - 1}
        onClick={() => moveHandler(index, 1)}
        size='small'
        sx={{p: '2px'}}
      >
        <KeyboardArrowDown sx={{fontSize: 16, color: index === itemCount - 1 ? '#E8E4DE' : '#A8A49E'}} />
      </IconButton>
    </Stack>
  );

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E8E4DE',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Stack sx={{gap: 2, px: 3, py: 2}}>
        <Stack sx={{gap: '4px'}}>
          <Typography sx={fieldLabelSx}>
            <Translate expression='domainGuide.Detail.Rubric.Title' />
          </Typography>
          <Typography sx={sectionHintSx}>
            <Translate expression='domainGuide.Detail.Rubric.Subtitle' />
          </Typography>
        </Stack>
        <Stack sx={{gap: '8px'}}>
          <Typography sx={fieldLabelSx}>
            <Translate expression='domainGuide.Detail.Rubric.FailureCategoriesLabel' />
          </Typography>
          <Typography sx={sectionHintSx}>
            <Translate expression='domainGuide.Detail.Rubric.FailureCategoriesHint' />
          </Typography>
          <Stack sx={{gap: 1.5}}>
            {
              editorState.categories.map((category, index) => (
                <Box
                  key={category.rowId}
                  sx={{
                    border: '1px solid #E8E4DE',
                    borderRadius: '8px',
                    p: 1.5,
                  }}
                >
                  <Stack sx={{gap: 1}}>
                    <Stack direction='row' sx={{gap: 1, alignItems: 'center'}}>
                      {
                        renderMoveButtons(
                            index,
                            editorState.categories.length,
                            moveCategory,
                            translate('domainGuide.Detail.Rubric.MoveCategoryUp'),
                            translate('domainGuide.Detail.Rubric.MoveCategoryDown'),
                        )
                      }
                      <TextField
                        fullWidth
                        inputProps={{maxLength: CATEGORY_LABEL_MAX_CHARS}}
                        placeholder={translate('domainGuide.Detail.Rubric.CategoryLabelPlaceholder')}
                        value={category.label}
                        onChange={(label) => setCategoryField(index, 'label', label)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                          },
                          '& .MuiOutlinedInput-input': {
                            py: '10px',
                          },
                        }}
                      />
                      <IconButton
                        aria-label={translate('domainGuide.Detail.Rubric.RemoveCategory')}
                        onClick={() => removeCategory(index)}
                        size='small'
                      >
                        <DeleteOutline sx={{fontSize: 18, color: '#A8A49E'}} />
                      </IconButton>
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      inputProps={{maxLength: CATEGORY_DESCRIPTION_MAX_CHARS}}
                      placeholder={translate('domainGuide.Detail.Rubric.CategoryDescriptionPlaceholder')}
                      value={category.description}
                      onChange={(description) => setCategoryField(index, 'description', description)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#6B6761',
                        },
                      }}
                    />
                  </Stack>
                </Box>
              ))
            }
          </Stack>
          <Button
            disabled={!canAddCategory}
            onClick={addCategory}
            startIcon={<AddOutlined sx={{fontSize: 16}} />}
            sx={{
              'alignSelf': 'flex-start',
              'px': '12px',
              'py': '6px',
              'border': '1px solid #E8E4DE',
              'borderRadius': '999px',
              'backgroundColor': '#FFFFFF',
              'boxShadow': 'none',
              'color': '#1A1916',
              'fontSize': '12px',
              'fontWeight': 700,
              'textTransform': 'none',
              '&:hover': {
                borderColor: '#D8D2CA',
                backgroundColor: '#F8F6F3',
                boxShadow: 'none',
              },
            }}
            variant='outlined'
          >
            <Translate expression='domainGuide.Detail.Rubric.AddCategory' />
          </Button>
        </Stack>
        <Stack sx={{gap: '8px'}}>
          <Typography sx={fieldLabelSx}>
            <Translate expression='domainGuide.Detail.Rubric.AutoFailConditionsLabel' />
          </Typography>
          <Typography sx={sectionHintSx}>
            <Translate expression='domainGuide.Detail.Rubric.AutoFailConditionsHint' />
          </Typography>
          <Stack sx={{gap: 1}}>
            {
              editorState.conditions.map((condition, index) => (
                <Stack
                  key={condition.rowId}
                  direction='row'
                  sx={{gap: 1, alignItems: 'center'}}
                >
                  {
                    renderMoveButtons(
                        index,
                        editorState.conditions.length,
                        moveCondition,
                        translate('domainGuide.Detail.Rubric.MoveConditionUp'),
                        translate('domainGuide.Detail.Rubric.MoveConditionDown'),
                    )
                  }
                  <TextField
                    fullWidth
                    multiline
                    inputProps={{maxLength: CONDITION_MAX_CHARS}}
                    placeholder={translate('domainGuide.Detail.Rubric.ConditionPlaceholder')}
                    value={condition.text}
                    onChange={(text) => setConditionText(index, text)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '13px',
                      },
                    }}
                  />
                  <IconButton
                    aria-label={translate('domainGuide.Detail.Rubric.RemoveCondition')}
                    onClick={() => removeCondition(index)}
                    size='small'
                  >
                    <DeleteOutline sx={{fontSize: 18, color: '#A8A49E'}} />
                  </IconButton>
                </Stack>
              ))
            }
          </Stack>
          {
            hasDuplicateConditions &&
            <Typography sx={{fontSize: '12px', color: '#D85D31'}}>
              <Translate expression='domainGuide.Detail.Rubric.DuplicateConditions' />
            </Typography>
          }
          <Button
            onClick={addCondition}
            startIcon={<AddOutlined sx={{fontSize: 16}} />}
            sx={{
              'alignSelf': 'flex-start',
              'px': '12px',
              'py': '6px',
              'border': '1px solid #E8E4DE',
              'borderRadius': '999px',
              'backgroundColor': '#FFFFFF',
              'boxShadow': 'none',
              'color': '#1A1916',
              'fontSize': '12px',
              'fontWeight': 700,
              'textTransform': 'none',
              '&:hover': {
                borderColor: '#D8D2CA',
                backgroundColor: '#F8F6F3',
                boxShadow: 'none',
              },
            }}
            variant='outlined'
          >
            <Translate expression='domainGuide.Detail.Rubric.AddCondition' />
          </Button>
        </Stack>
        <Stack sx={{gap: '8px'}}>
          <Typography sx={fieldLabelSx}>
            <Translate expression='domainGuide.Detail.Rubric.PassingDefinitionLabel' />
          </Typography>
          <Typography sx={sectionHintSx}>
            <Translate expression='domainGuide.Detail.Rubric.PassingDefinitionHint' />
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            inputProps={{maxLength: PASSING_DEFINITION_MAX_CHARS}}
            placeholder={translate('domainGuide.Detail.Rubric.PassingDefinitionPlaceholder')}
            value={editorState.passingDefinition}
            onChange={setPassingDefinition}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                fontSize: '14px',
                lineHeight: '150%',
              },
            }}
          />
        </Stack>
        <Stack direction='row' sx={{gap: 2, alignItems: 'center'}}>
          <Tooltip title={canSave ? '' : translate('domainGuide.Detail.Rubric.CannotSaveHint')}>
            <Box>
              <Button
                disabled={!canSave || isSaving}
                onClick={saveRubric}
                sx={{
                  'px': '16px',
                  'py': '8px',
                  'borderRadius': '8px',
                  'backgroundColor': '#DA7955',
                  'boxShadow': 'none',
                  'color': '#FFFFFF',
                  'fontSize': '13px',
                  'fontWeight': 700,
                  'textTransform': 'none',
                  '&:hover': {
                    backgroundColor: '#C4512C',
                    boxShadow: 'none',
                  },
                }}
                variant='contained'
              >
                {
                  isSaving ?
                    translate('domainGuide.Detail.Rubric.Saving') :
                    translate('domainGuide.Detail.Rubric.Save')
                }
              </Button>
            </Box>
          </Tooltip>
          {
            saveFailed &&
            <Typography sx={{fontSize: '12px', color: '#D85D31'}}>
              <Translate expression='domainGuide.Detail.Rubric.SaveFailed' />
            </Typography>
          }
          {
            !saveFailed &&
            <Typography sx={{fontSize: '12px', color: '#A8A49E'}}>
              {
                domainGuideData.rubric && rubricUpdatedAtLabel ?
                  translate('domainGuide.Detail.Rubric.UpdatedAt', {updatedAt: rubricUpdatedAtLabel}) :
                  translate('domainGuide.Detail.Rubric.NeverSaved')
              }
            </Typography>
          }
        </Stack>
      </Stack>
    </Box>
  );
};

DomainGuideRubric.propTypes = {
  domainGuideData: PropTypes.object.isRequired,
  updateRubric: PropTypes.func.isRequired,
};

export default React.memo(DomainGuideRubric);
